import { STALE_DIM_OPACITY } from '@/design/bayStates';
import { formatFacilityTime, toDate, type Instant } from '@/lib/time';
import { formatDuration } from '@/lib/format';

/**
 * The freshness ladder — W-1.5. One implementation, used by every live view.
 *
 * The governing rule is §9.1: **values are never hidden.** Under staleness a
 * figure dims and grows an "as at" suffix; it does not disappear. An operator
 * with old data and a warning is better served than one with a blank screen,
 * because the blank screen tells them nothing about whether the car park is
 * full or the network is down.
 */

/** `/live/*` and `/bays/live` poll every 10 s. */
export const LIVE_POLL_INTERVAL_MS = 10_000;

/** Signage content polls every 30 s (its own `ttlSeconds` is 30). */
export const SIGNAGE_POLL_INTERVAL_MS = 30_000;

/** Three consecutive failed polls is what "disconnected" means. */
export const DISCONNECT_AFTER_CONSECUTIVE_FAILURES = 3;

/** Exponential back-off for the automatic retry, capped so it keeps trying. */
export const RETRY_BACKOFF_BASE_MS = 2_000;
export const RETRY_BACKOFF_MAX_MS = 60_000;

export type FreshnessStage =
  /** Within one poll interval. Plain — no chrome at all. */
  | 'fresh'
  /** Between one and two intervals. Amber, but nothing dims yet. */
  | 'aging'
  /** Beyond two intervals. Amber banner, figures dimmed, "as at" suffix. */
  | 'stale'
  /** Three consecutive failed polls. Red banner, values frozen. */
  | 'disconnected';

export interface FreshnessInput {
  /**
   * The newest `observedAt` in the visible set — not the time the request
   * returned. A cached 200 with a two-minute-old payload is stale data.
   */
  readonly observedAt: Instant | null | undefined;
  /** Consecutive failed polls, from the query's `failureCount`. */
  readonly consecutiveFailures?: number;
  /** When the last poll actually succeeded — the timestamp the red banner names. */
  readonly lastSuccessAt?: Instant | null;
  readonly pollIntervalMs?: number;
  readonly now?: Instant;
}

export interface Freshness {
  readonly stage: FreshnessStage;
  /** Age of the data in seconds; `null` when nothing has ever loaded. */
  readonly ageSeconds: number | null;
  /** Opacity for live figures and bay fills: 1 until stale, then 0.6. */
  readonly dimOpacity: number;
  /** True once the counter strip must carry `as at 08:41`. */
  readonly showAsAt: boolean;
  /** `as at 08:41`, or `null` while fresh. */
  readonly asAtLabel: string | null;
  /** Human age for the banner — `2 h 14 m`. `null` while fresh. */
  readonly ageLabel: string | null;
  /** Milliseconds to wait before the next automatic retry, when disconnected. */
  readonly retryAfterMs: number | null;
}

/** Exponential back-off, capped at {@link RETRY_BACKOFF_MAX_MS}. */
export function retryBackoffMs(consecutiveFailures: number): number {
  const attempt = Math.max(1, consecutiveFailures);
  return Math.min(RETRY_BACKOFF_MAX_MS, RETRY_BACKOFF_BASE_MS * 2 ** (attempt - 1));
}

export function resolveFreshness(input: FreshnessInput): Freshness {
  const {
    observedAt,
    consecutiveFailures = 0,
    lastSuccessAt = null,
    pollIntervalMs = LIVE_POLL_INTERVAL_MS,
    now = Date.now(),
  } = input;

  // Disconnected outranks age: three failed polls means we do not know how old
  // the data is going to get, and the honest thing is to freeze and say so.
  if (consecutiveFailures >= DISCONNECT_AFTER_CONSECUTIVE_FAILURES) {
    const frozenAt = lastSuccessAt ?? observedAt ?? null;
    const age = frozenAt ? exactAgeSeconds(frozenAt, now) : null;
    return {
      stage: 'disconnected',
      ageSeconds: age,
      dimOpacity: STALE_DIM_OPACITY,
      showAsAt: frozenAt !== null,
      asAtLabel: frozenAt ? `as at ${formatFacilityTime(frozenAt)}` : null,
      ageLabel: age === null ? null : formatDuration(Math.round(age), 'coarse'),
      retryAfterMs: retryBackoffMs(consecutiveFailures),
    };
  }

  if (observedAt === null || observedAt === undefined) {
    return {
      stage: 'fresh',
      ageSeconds: null,
      dimOpacity: 1,
      showAsAt: false,
      asAtLabel: null,
      ageLabel: null,
      retryAfterMs: null,
    };
  }

  // Staging is compared on the exact age; only the reported value is rounded,
  // so a boundary of exactly one interval does not flip on a rounding rule.
  const age = exactAgeSeconds(observedAt, now);
  const reported = Math.round(age);
  const intervalSeconds = pollIntervalMs / 1000;

  if (age < intervalSeconds) {
    return {
      stage: 'fresh',
      ageSeconds: reported,
      dimOpacity: 1,
      showAsAt: false,
      asAtLabel: null,
      ageLabel: null,
      retryAfterMs: null,
    };
  }

  if (age < intervalSeconds * 2) {
    return {
      stage: 'aging',
      ageSeconds: reported,
      dimOpacity: 1,
      showAsAt: false,
      asAtLabel: null,
      ageLabel: formatDuration(reported, 'coarse'),
      retryAfterMs: null,
    };
  }

  return {
    stage: 'stale',
    ageSeconds: reported,
    dimOpacity: STALE_DIM_OPACITY,
    showAsAt: true,
    asAtLabel: `as at ${formatFacilityTime(observedAt)}`,
    ageLabel: formatDuration(reported, 'coarse'),
    retryAfterMs: null,
  };
}

/** Fractional seconds, so a threshold comparison is not decided by rounding. */
function exactAgeSeconds(instant: Instant, now: Instant): number {
  return (toDate(now).getTime() - toDate(instant).getTime()) / 1000;
}

/** The newest `observedAt` across several live queries — the ladder's input. */
export function newestObservedAt(
  candidates: readonly (Instant | null | undefined)[],
): Instant | null {
  let newest: number | null = null;
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;
    const ms = candidate instanceof Date ? candidate.getTime() : new Date(candidate).getTime();
    if (Number.isNaN(ms)) continue;
    if (newest === null || ms > newest) newest = ms;
  }
  return newest === null ? null : new Date(newest);
}
